import { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ColumnDef,
  Header,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { DateTime } from 'luxon'
import { FiAlertTriangle, FiCalendar, FiCheckCircle, FiWifi, FiWifiOff } from 'react-icons/fi'
import { MdBattery20, MdLightMode, MdThermostat, MdWaterDrop } from 'react-icons/md'
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from 'react-icons/ti'

import { useDashboard } from '@/hooks'

import { DEFAULT_OPTIONAL_COLUMN_IDS, PINNED_COLUMN_IDS, isPinnedColumn } from './trackerColumnConfig'
import { useTrackerMapContext } from './TrackerMapContext'
import { RowStatus, Severity, TrackerShipmentRow } from './types'
import {
  SHIPMENT_STATE_BADGE_CLASS,
  SHIPMENT_STATE_LABELS,
  formatLux,
  formatPercent,
  formatTemp,
  getRowStatus,
  rowId,
  rowKey,
  tsToMillis,
} from './utils'

interface TrackerTableProps {
  trackers: TrackerShipmentRow[]
  currentRowId?: number
  onTrackerClick: (row: TrackerShipmentRow) => void
  /** The detail sheet sits beside the list: show only the pinned columns, so it starts after Alert. */
  pinnedOnly?: boolean
  /** The header row's viewport rect, with `right` at the end of the visible columns. */
  onHeaderMeasured?: (rect: { top: number; height: number; right: number }) => void
}

interface ColMeta {
  width: number
  icon?: ReactNode
  center?: boolean
}

// Most-recent sample epoch across location/temp/battery (0 when none) — numeric so
// the column sorts chronologically.
const lastReportedMillis = (t: TrackerShipmentRow): number =>
  Math.max(
    0,
    ...[tsToMillis(t.locationTs), tsToMillis(t.temperatureCelsiusTs), tsToMillis(t.batteryPercentTs)].filter(
      (v): v is number => v != null
    )
  )

// Trackers carry no address/geofence yet, so Location shows coordinates for now.
const formatLocation = (location: TrackerShipmentRow['location']): string =>
  location ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}` : '—'

// Alert icon, colored by severity, mirroring the Asset List. Null (no active alert) renders nothing.
const alertIcon = (severity?: Severity | null) => {
  switch (severity) {
    case 'critical':
      return <FiAlertTriangle className="w-4 h-4 text-red-500" />
    case 'warning':
      return <FiAlertTriangle className="w-4 h-4 text-yellow-500" />
    case 'informational':
      return <FiAlertTriangle className="w-4 h-4 text-green-500" />
    default:
      return null
  }
}

// Status icon per row: online/offline for live tracker rows, calendar for
// shipments with no tracker yet, checkmark for rows frozen at shipment end.
// Shared with the quick view.
export const rowStatusIcon = (status: RowStatus) => {
  switch (status) {
    case 'planned':
      return <FiCalendar className="w-4 h-4 text-violet-500" title="No tracker assigned" />
    case 'completed':
      return <FiCheckCircle className="w-4 h-4 text-base-content/50" title="Completed" />
    case 'stale':
      return <FiWifiOff className="w-4 h-4 text-gray-400" title="Offline" />
    default:
      return <FiWifi className="w-4 h-4 text-emerald-600" title="Online" />
  }
}

// Sort arrow: hidden until the header row is hovered; the active sort stays visible.
const sortIcon = (sorted: false | 'asc' | 'desc') => {
  if (!sorted) return <TiArrowUnsorted className="text-base opacity-0 transition-opacity group-hover:opacity-100" />
  if (sorted === 'desc') return <TiArrowSortedDown className="text-base" />
  return <TiArrowSortedUp className="text-base" />
}

// Shared header content: icon-only (with tooltip) for metric/status/alert columns, else the label.
// Centered (icon-only-cell) columns park the sort arrow at the right edge so the reserved
// arrow space doesn't push the header icon off-center relative to the cell icons below.
const headerContent = (header: Header<TrackerShipmentRow, unknown>) => {
  const meta = header.column.columnDef.meta as ColMeta
  return (
    <>
      {meta.icon ? (
        <span className="tooltip tooltip-bottom flex items-center" data-tip={header.column.columnDef.header as string}>
          {meta.icon}
        </span>
      ) : (
        <span className="truncate">{flexRender(header.column.columnDef.header, header.getContext())}</span>
      )}
      {meta.center ? (
        <span className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
          {sortIcon(header.column.getIsSorted())}
        </span>
      ) : (
        sortIcon(header.column.getIsSorted())
      )}
    </>
  )
}

const thClass = (meta: ColMeta, pinned: boolean) =>
  `shrink-0 px-3 py-2 font-semibold cursor-pointer select-none whitespace-nowrap border-b border-base-300 relative flex items-center gap-1 ${
    meta.center ? 'justify-center' : 'text-left'
  } ${pinned ? 'bg-base-200 z-10' : ''}`

// Pinned header: fixed in place, no drag handle.
const PinnedHeader = ({ header }: { header: Header<TrackerShipmentRow, unknown> }) => {
  const meta = header.column.columnDef.meta as ColMeta
  return (
    <th
      onClick={header.column.getToggleSortingHandler()}
      style={{ width: `${meta.width}px` }}
      className={thClass(meta, true)}
    >
      {headerContent(header)}
    </th>
  )
}

// Draggable header: reorderable via a grab handle.
const DraggableHeader = ({ header }: { header: Header<TrackerShipmentRow, unknown> }) => {
  const meta = header.column.columnDef.meta as ColMeta
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: header.column.id })
  const style: CSSProperties = {
    width: `${meta.width}px`,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <th
      ref={setNodeRef}
      onClick={header.column.getToggleSortingHandler()}
      style={style}
      className={thClass(meta, false)}
    >
      {headerContent(header)}
      <span
        {...listeners}
        {...attributes}
        onClick={(e) => e.stopPropagation()}
        title="Drag to reorder"
        className="ml-auto cursor-grab text-base-content/30 hover:text-base-content/70"
      >
        ⋮⋮
      </span>
    </th>
  )
}

export const TrackerTable = ({
  trackers,
  currentRowId,
  onTrackerClick,
  pinnedOnly = false,
  onHeaderMeasured,
}: TrackerTableProps) => {
  const { visibleColumns, setVisibleColumns } = useTrackerMapContext()
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const [sorting, setSorting] = useState<SortingState>([{ id: 'shipment', desc: false }])
  const containerRef = useRef<HTMLDivElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  )

  const columns = useMemo<ColumnDef<TrackerShipmentRow>[]>(
    () => [
      {
        id: 'shipment',
        header: 'Shipment',
        accessorFn: (t) => t.shipmentName ?? '',
        cell: (c) => {
          const row = c.row.original
          if (!row.shipmentId) return <span className="italic text-base-content/50">Unassigned</span>
          return (
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-medium truncate">{row.shipmentName}</span>
              {row.shipmentState && (
                <span className={`badge badge-xs shrink-0 ${SHIPMENT_STATE_BADGE_CLASS[row.shipmentState]}`}>
                  {SHIPMENT_STATE_LABELS[row.shipmentState]}
                </span>
              )}
            </span>
          )
        },
        meta: { width: 230 } as ColMeta,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (t) => getRowStatus(t),
        cell: (c) => rowStatusIcon(c.getValue<RowStatus>()),
        meta: { width: 72, center: true, icon: <FiWifi className="w-4 h-4 text-emerald-600" /> } as ColMeta,
      },
      {
        id: 'alert',
        header: 'Alert',
        accessorFn: (t) => t.highestAlertSeverity ?? '',
        cell: (c) => alertIcon(c.row.original.highestAlertSeverity),
        meta: {
          width: 72,
          center: true,
          icon: <FiAlertTriangle className="w-4 h-4 text-base-content/60" />,
        } as ColMeta,
      },
      {
        accessorKey: 'temperatureCelsius',
        header: 'Temp',
        cell: (c) => formatTemp(c.getValue<number | null>(), units),
        meta: { width: 100, icon: <MdThermostat className="w-4 h-4 text-rose-500" /> } as ColMeta,
      },
      {
        accessorKey: 'humidityPercent',
        header: 'Humidity',
        cell: (c) => formatPercent(c.getValue<number | null>()),
        meta: { width: 120, icon: <MdWaterDrop className="w-4 h-4 text-sky-500" /> } as ColMeta,
      },
      {
        accessorKey: 'lightLux',
        header: 'Light',
        cell: (c) => formatLux(c.getValue<number | null>()),
        meta: { width: 120, icon: <MdLightMode className="w-4 h-4 text-amber-500" /> } as ColMeta,
      },
      {
        accessorKey: 'batteryPercent',
        header: 'Battery',
        cell: (c) => formatPercent(c.getValue<number | null>()),
        meta: { width: 110, icon: <MdBattery20 className="w-4 h-4 text-emerald-600" /> } as ColMeta,
      },
      {
        id: 'location',
        header: 'Location',
        accessorFn: (t) => formatLocation(t.location),
        cell: (c) => c.getValue<string>(),
        meta: { width: 200 } as ColMeta,
      },
      {
        id: 'lastReported',
        header: 'Date Last Reported',
        accessorFn: (t) => lastReportedMillis(t),
        cell: (c) => {
          const ms = c.getValue<number>()
          return ms ? DateTime.fromMillis(ms).toFormat('yyyy-MM-dd HH:mm') : '—'
        },
        meta: { width: 170 } as ColMeta,
      },
      {
        accessorKey: 'serialNumber',
        header: 'Serial Number',
        cell: (c) => c.getValue<string | null>() ?? '—',
        meta: { width: 150 } as ColMeta,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (t) => t.label ?? '',
        cell: (c) => <span className="font-medium">{c.getValue<string>() || '—'}</span>,
        meta: { width: 200 } as ColMeta,
      },
    ],
    [units]
  )

  // Pinned columns first, then the user's visible optional columns in their chosen order, then the
  // hidden optional columns (kept in the order list so react-table has a stable, complete ordering).
  const columnOrder = useMemo(() => {
    const hidden = DEFAULT_OPTIONAL_COLUMN_IDS.filter((id) => !visibleColumns.includes(id))
    return [...PINNED_COLUMN_IDS, ...visibleColumns, ...hidden]
  }, [visibleColumns])

  const columnVisibility = useMemo<VisibilityState>(() => {
    const v: VisibilityState = {}
    DEFAULT_OPTIONAL_COLUMN_IDS.forEach((id) => {
      v[id] = !pinnedOnly && visibleColumns.includes(id)
    })
    return v
  }, [visibleColumns, pinnedOnly])

  const table = useReactTable({
    data: trackers,
    columns,
    state: { sorting, columnOrder, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const rows = table.getRowModel().rows
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 36,
    overscan: 10,
  })

  const totalWidth = table
    .getVisibleLeafColumns()
    .reduce((sum, c) => sum + ((c.columnDef.meta as ColMeta).width ?? 120), 0)

  // Where the header row is and where its columns end (the row itself spans the panel), so the
  // detail sheet can start right after the last column, level with the header.
  useEffect(() => {
    const thead = theadRef.current
    const container = containerRef.current
    if (!onHeaderMeasured || !thead || !container) return
    const measure = () => {
      const rect = thead.getBoundingClientRect()
      // Never past the list's own edge: a narrow panel scrolls its columns rather than showing them all.
      const right = Math.min(rect.left + totalWidth - container.scrollLeft, container.getBoundingClientRect().right)
      onHeaderMeasured({ top: rect.top, height: rect.height, right })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    observer.observe(thead)
    return () => observer.disconnect()
  }, [onHeaderMeasured, totalWidth])

  // Keep the selected row in view, e.g. after picking it on the map.
  const selectedIndex = rows.findIndex((r) => rowId(r.original) === currentRowId)
  useEffect(() => {
    if (selectedIndex >= 0) virtualizer.scrollToIndex(selectedIndex, { align: 'auto' })
  }, [selectedIndex, virtualizer])

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = visibleColumns.indexOf(active.id as string)
    const newIndex = visibleColumns.indexOf(over.id as string)
    if (oldIndex < 0 || newIndex < 0) return
    setVisibleColumns(arrayMove(visibleColumns, oldIndex, newIndex))
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-auto">
      <table className="text-sm border-collapse" style={{ minWidth: `${totalWidth}px`, width: '100%' }}>
        <thead ref={theadRef} className="sticky top-0 z-10 bg-base-200">
          <DndContext
            sensors={sensors}
            modifiers={[restrictToHorizontalAxis]}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="group flex w-full">
                <SortableContext items={visibleColumns} strategy={horizontalListSortingStrategy}>
                  {hg.headers.map((header) =>
                    isPinnedColumn(header.column.id) ? (
                      <PinnedHeader key={header.id} header={header} />
                    ) : (
                      <DraggableHeader key={header.id} header={header} />
                    )
                  )}
                </SortableContext>
              </tr>
            ))}
          </DndContext>
        </thead>
        <tbody style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative', display: 'block' }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index]
            const tracker = row.original
            return (
              <tr
                key={rowKey(tracker)}
                onClick={() => onTrackerClick(tracker)}
                className={`cursor-pointer border-b border-base-200 flex w-full ${
                  rowId(tracker) === currentRowId ? 'bg-sky-100' : 'hover:bg-base-200'
                }`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as ColMeta
                  return (
                    <td
                      key={cell.id}
                      style={{ width: `${meta.width}px` }}
                      className={`shrink-0 px-3 py-2 whitespace-nowrap truncate flex items-center ${
                        meta.center ? 'justify-center' : ''
                      }`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
